import React, { Fragment, useState, useMemo } from 'react';
import Link from 'shared/SlashLink';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import { WEB3FORMS_ACCESS_KEY } from '../../config/web3forms';

const ClickHandler = () => window.scrollTo(10, 0);

const INCLUDES = [
    { icon: 'fa-magnifying-glass-chart', title: 'SEO-Optimized', text: 'Clean structure, meta tags, and a submitted sitemap so Google can find you from day one.' },
    { icon: 'fa-chart-line', title: 'Analytics-Ready', text: 'Google Analytics 4 wired in so you see every visitor, source, and conversion.' },
    { icon: 'fa-mobile-screen', title: 'Mobile-Responsive', text: 'Looks sharp on every phone, tablet, and desktop — no pinching, no zooming.' },
    { icon: 'fa-bolt', title: 'Lightning Fast', text: 'Optimized images and code for fast load times that keep visitors from bouncing.' },
    { icon: 'fa-lock', title: 'SSL Secured', text: 'HTTPS and security headers configured so visitors (and Google) trust your site.' },
    { icon: 'fa-envelope-open-text', title: 'Lead-Capture Forms', text: 'Contact forms that actually deliver to your inbox — no leads lost to a dead button.' },
    { icon: 'fa-location-dot', title: 'Google Business Ready', text: 'Structured so your business shows up in local search and on Google Maps.' },
    { icon: 'fa-pen-ruler', title: 'Custom, Not Cookie-Cutter', text: 'Designed around your brand and colors — not a template everyone else is using.' },
];

const STEPS = [
    { n: '01', title: 'Tell us about your site', text: 'Fill out the form below. It takes two minutes and tells us your scope.' },
    { n: '02', title: 'We confirm same-day feasibility', text: 'We review your answers and confirm whether your site qualifies for the 24-hour build.' },
    { n: '03', title: 'Your site goes live', text: 'Approve the design, and we launch — SEO-optimized, analytics-ready, and mobile-perfect.' },
];

const FEATURE_OPTIONS = [
    'Contact / lead form',
    'Online booking or scheduling',
    'Photo gallery',
    'Blog / news section',
    'Online store (e-commerce)',
    'Multi-language',
];

// Starting prices, informed by typical market rush-build ranges. Final quote
// depends on scope — see the "what affects your price" factors below.
const PRICING = [
    {
        name: 'Landing Page', price: '$499', sub: 'starting',
        blurb: 'A polished one-page site to get you online today.',
        features: ['Single page, custom design', 'Mobile-responsive + SSL', 'Lead-capture contact form', 'Basic on-page SEO + GA4', 'Live in 24 hours'],
        best: false,
    },
    {
        name: 'Business Site', price: '$1,200', sub: 'starting',
        blurb: 'A complete small-business presence — 3 to 7 pages.',
        features: ['3–7 custom pages', 'Brand-matched styling', 'Lead forms + Google Business', 'On-page SEO + analytics', 'Same-day / next-day build'],
        best: true,
    },
    {
        name: 'Store / Booking', price: '$2,800', sub: 'starting',
        blurb: 'Sell products or take bookings and payments online.',
        features: ['Everything in Business Site', 'Product or booking setup', 'Secure checkout / payments', 'Up to ~20 products or services', 'Typically 1–3 days'],
        best: false,
    },
    {
        name: 'Custom Build', price: 'Custom', sub: 'quote',
        blurb: 'Full design, development, and content for complex sites.',
        features: ['Custom design + development', 'Integrations, logins, databases', 'Copywriting + media production', 'Multi-language & advanced features', 'Scoped to your timeline'],
        best: false,
    },
];

const PRICE_FACTORS = [
    'Content creation — copywriting, photography, video',
    'Custom design or complex functionality',
    'E-commerce, integrations, logins, or databases',
    'Same-day rush turnaround (rush premium applies)',
    'Hosting, domain, SSL, and ongoing support',
];

// Qualification-form styling — matches the self-contained "scoped CSS + local
// hex constants + Axiforma" pattern already established by LowVoltageRequest.js
// / ToolStudio.js elsewhere in this app, rather than this page's older bare
// Bootstrap .form-control styling. Prefixed .sdw__ (Same Day Websites) to
// avoid colliding with this page's existing global/Bootstrap classes.
const NAVY = '#020842', BLUE = '#0044EB', PINK = '#F44380', INK = '#16203C', SLATE = '#54648A', LINE = '#D7E2F1';
const GREEN = '#1F9D57', GREEN_BG = '#E7F6EE';
const AMBER = '#B45309', AMBER_BG = '#FFF7ED', AMBER_LINE = '#F3C88C';
const BLUE_BG = '#EEF3FF';

const VERDICT_META = {
    yes: { fg: GREEN, bg: GREEN_BG, line: '#BFE7D2' },
    maybe: { fg: AMBER, bg: AMBER_BG, line: AMBER_LINE },
    custom: { fg: BLUE, bg: BLUE_BG, line: '#C7D8FF' },
};

const SDW_CSS = `
.sdw__card{background:#fff;border:1px solid ${LINE};border-radius:20px;padding:clamp(22px,4vw,40px);box-shadow:0 30px 70px rgba(6,16,54,.08);margin-top:24px}
.sdw__grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.sdw__f{margin-bottom:16px}
.sdw__f label{display:block;font-family:'Axiforma SemiBold',sans-serif;color:${INK};font-size:13.5px;margin-bottom:7px}
.sdw__req{color:${PINK};margin-left:2px}
.sdw__f input[type=text],.sdw__f input[type=email],.sdw__f input[type=tel],.sdw__f textarea{
  width:100%;border:1.5px solid ${LINE};border-radius:12px;padding:12px 14px;
  font-family:'Axiforma Regular',sans-serif;font-size:15px;color:${INK};background:#F7FAFD;
  transition:border-color .15s,box-shadow .15s,background-color .15s;}
.sdw__f input::placeholder,.sdw__f textarea::placeholder{color:#9AA7C2}
.sdw__f input:hover:not(:focus),.sdw__f textarea:hover:not(:focus){border-color:#B9CBEA}
.sdw__f input:focus,.sdw__f textarea:focus{outline:none;border-color:${BLUE};background:#fff;box-shadow:0 0 0 4px rgba(0,68,235,.10)}
.sdw__f textarea{resize:vertical;min-height:88px}
.sdw__seg{display:inline-flex;flex-wrap:wrap;background:#F1F5FB;border:1.5px solid ${LINE};border-radius:13px;padding:4px;gap:3px}
.sdw__segbtn{border:none;background:transparent;border-radius:9px;padding:9px 17px;font-family:'Axiforma SemiBold',sans-serif;font-size:13.5px;color:${SLATE};cursor:pointer;transition:.15s;white-space:nowrap}
.sdw__segbtn:hover:not([aria-pressed=true]){color:${INK}}
.sdw__segbtn[aria-pressed=true]{background:#fff;color:${BLUE};box-shadow:0 3px 10px rgba(6,16,54,.12)}
.sdw__opts{display:flex;flex-wrap:wrap;gap:9px}
.sdw__chip{background:#F7FAFD;border:1.5px solid ${LINE};border-radius:999px;padding:10px 16px;cursor:pointer;font-family:'Axiforma SemiBold',sans-serif;color:${INK};font-size:13.5px;transition:.15s}
.sdw__chip:hover{border-color:#B9CBEA;background:#fff}
.sdw__chip[aria-pressed=true]{border-color:${BLUE};background:${BLUE};color:#fff}
.sdw__verdict{border-radius:16px;padding:18px 20px;margin:6px 0 20px;display:flex;gap:14px;align-items:flex-start}
.sdw__verdicticon{flex:none;width:38px;height:38px;border-radius:10px;display:flex;align-items:center;justify-content:center}
.sdw__verdictt{font-family:'Axiforma Bold',sans-serif;font-size:16px;margin:0 0 4px}
.sdw__verdictd{font-family:'Axiforma Regular',sans-serif;font-size:14.5px;line-height:1.55;margin:0;color:${INK}}
.sdw__err{background:#FDECEC;border:1.5px solid #F6C6C6;border-radius:14px;padding:14px 18px;margin-bottom:18px;font-family:'Axiforma SemiBold',sans-serif;color:#B42318;font-size:14px}
.sdw__btn{display:inline-flex;align-items:center;justify-content:center;background:${BLUE};color:#fff;border:none;border-radius:12px;padding:16px 28px;font-family:'Axiforma Bold',sans-serif;font-size:16px;cursor:pointer;transition:.15s;width:100%}
.sdw__btn:hover{background:#0037c4}
.sdw__btn:disabled{opacity:.55;cursor:not-allowed}
.sdw__done{text-align:center;padding:40px 10px}
.sdw__done h3{font-family:'Axiforma Bold',sans-serif;color:${NAVY};font-size:26px;margin:16px 0 10px}
.sdw__done p{font-family:'Axiforma Regular',sans-serif;color:${SLATE};font-size:16px;line-height:1.6;max-width:480px;margin:0 auto}
@media(max-width:760px){.sdw__grid{grid-template-columns:1fr}}
`;

const SdwField = ({ label, required, children }) => (
    <div className="sdw__f">
        <label>{label}{required && <span className="sdw__req">*</span>}</label>
        {children}
    </div>
);

// Joined toggle bar — short single-select choices (pages / content-ready / domain).
const SdwSegmented = ({ options, value, onChange }) => (
    <div className="sdw__seg" role="group">
        {options.map((o) => (
            <button type="button" key={o} className="sdw__segbtn" aria-pressed={value === o} onClick={() => onChange(o)}>{o}</button>
        ))}
    </div>
);

// Flowing pills — multi-select feature list.
const SdwChips = ({ options, value, onToggle }) => (
    <div className="sdw__opts">
        {options.map((o) => (
            <button type="button" key={o} className="sdw__chip" aria-pressed={value.includes(o)} onClick={() => onToggle(o)}>{o}</button>
        ))}
    </div>
);

const SameDayWebsitesPage = () => {
    usePageSeo({
        title: 'Same Day Websites — Professional Site Live in 24 Hours',
        description: 'BUILDLAB Same Day Websites: a professional, SEO-optimized, analytics-ready, mobile-responsive website live in 24 hours. Find out if your site qualifies for a one-day build.',
        keywords: 'same day website, 24 hour website, fast website design, SEO optimized website, analytics ready, small business website, mobile responsive, BUILDLAB web development',
    });

    const [form, setForm] = useState({
        name: '', email: '', phone: '', business: '',
        pages: '', content: '', domain: '', notes: '',
        features: [],
    });
    const [status, setStatus] = useState('idle'); // idle | submitting | success | error

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleFeature = (feat) => setForm((f) => ({
        ...f,
        features: f.features.includes(feat) ? f.features.filter((x) => x !== feat) : [...f.features, feat],
    }));

    // Live same-day feasibility estimate
    const verdict = useMemo(() => {
        if (!form.pages || !form.content) return null;
        const heavy = form.features.includes('Online store (e-commerce)') || form.features.includes('Multi-language');
        const bigSite = form.pages === '10+ pages' || form.pages === '6–10 pages';
        const noContent = form.content === 'No — I need help creating it';
        if (heavy || form.pages === '10+ pages' || noContent) {
            return { level: 'custom', label: 'Likely a custom timeline', text: 'Your project has scope that usually runs beyond a single day — we’ll scope it and give you a fast, honest timeline.' };
        }
        if (bigSite || form.content === 'Partially') {
            return { level: 'maybe', label: 'Possible in 1–2 days', text: 'You’re close. With your content ready, this is often a same-day or next-day build.' };
        }
        return { level: 'yes', label: 'Strong same-day candidate', text: 'Based on your answers, your site is a great fit for the 24-hour build.' };
    }, [form.pages, form.content, form.features]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.pages || !form.content) {
            setStatus('error');
            return;
        }
        setStatus('submitting');
        try {
            const res = await fetch('https://api.web3forms.com/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    access_key: WEB3FORMS_ACCESS_KEY,
                    subject: 'Same Day Website inquiry — BUILDLAB website',
                    from_name: 'BUILDLAB Same Day Websites',
                    name: form.name,
                    email: form.email,
                    phone: form.phone,
                    business: form.business,
                    pages: form.pages,
                    content_ready: form.content,
                    has_domain: form.domain,
                    features: form.features.join(', ') || 'None selected',
                    same_day_estimate: verdict ? verdict.label : 'Not computed',
                    notes: form.notes,
                }),
            });
            const data = await res.json();
            if (data.success) { setStatus('success'); }
            else { setStatus('error'); }
        } catch (err) { setStatus('error'); }
    };

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={'Same Day Websites'} pagesub={'Web Development'} pageTop={'BUILDLAB'} />

                {/* Hero pitch */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="text-center" style={{ maxWidth: 780, margin: '0 auto' }}>
                            <span style={{
                                display: 'inline-block', background: 'var(--bs-primary-bg-subtle)', color: 'var(--bs-primary)',
                                fontWeight: 700, fontSize: '.8rem', letterSpacing: '.08em', textTransform: 'uppercase',
                                padding: '6px 16px', borderRadius: 20, marginBottom: 18,
                            }}>Live in 24 hours</span>
                            <h2 className="heading_text mb-3" style={{ fontSize: '2.4rem' }}>Your professional website — built and launched in a single day</h2>
                            <p className="heading_description mb-4">
                                Need a website fast without the agency runaround? BUILDLAB builds clean, SEO-optimized, analytics-ready
                                sites that go live in 24 hours. Mobile-responsive, secure, and wired to capture leads from the first visitor.
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
                                {['SEO-Optimized', 'Analytics-Ready', 'Mobile-Responsive', 'SSL Secured', 'Lead Forms Built In', 'Google Business Ready'].map((chip) => (
                                    <span key={chip} style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 20, padding: '6px 14px', fontSize: '.82rem', fontWeight: 600, color: 'var(--bs-dark)' }}>
                                        <i className="fa-solid fa-check" aria-hidden="true" style={{ color: 'var(--bs-primary)', marginRight: 6 }}></i>{chip}
                                    </span>
                                ))}
                            </div>
                            <div className="mt-4">
                                <a href="#qualify" className="btn btn-primary" onClick={(e) => { e.preventDefault(); document.getElementById('qualify')?.scrollIntoView({ behavior: 'smooth' }); }}>
                                    <span className="btn_label" data-text="Check My Site">Check If My Site Qualifies</span>
                                    <span className="btn_icon"><i className="fa-solid fa-arrow-down" aria-hidden="true"></i></span>
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                {/* What's included */}
                <section className="section_space">
                    <div className="container">
                        <div className="heading_block text-center">
                            <div className="heading_focus_text">
                                Every site
                                <span className="badge bg-secondary text-white">includes</span>
                            </div>
                            <h2 className="heading_text mb-0">Built right, not just built fast</h2>
                        </div>
                        <div className="row">
                            {INCLUDES.map((item, i) => (
                                <div className="col-lg-3 col-md-6 mt-4" key={i}>
                                    <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 12, padding: '24px 20px', height: '100%' }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 10, marginBottom: 14, background: 'var(--bs-primary-bg-subtle)', color: 'var(--bs-primary)', fontSize: 20 }}>
                                            <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
                                        </span>
                                        <h3 style={{ fontSize: '1.05rem', color: 'var(--bs-dark)', marginBottom: 6 }}>{item.title}</h3>
                                        <p className="mb-0" style={{ fontSize: '.88rem' }}>{item.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* How it works */}
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="heading_block text-center">
                            <div className="heading_focus_text">
                                How it
                                <span className="badge bg-secondary text-white">works</span>
                            </div>
                            <h2 className="heading_text mb-0">Three steps to launch</h2>
                        </div>
                        <div className="row">
                            {STEPS.map((s, i) => (
                                <div className="col-md-4 mt-4" key={i}>
                                    <div style={{ padding: '8px 12px' }}>
                                        <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--bs-primary)', opacity: 0.4 }}>{s.n}</div>
                                        <h3 style={{ fontSize: '1.15rem', color: 'var(--bs-dark)', margin: '6px 0 8px' }}>{s.title}</h3>
                                        <p className="mb-0" style={{ fontSize: '.9rem' }}>{s.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Pricing */}
                <section className="section_space">
                    <div className="container">
                        <div className="heading_block text-center">
                            <div className="heading_focus_text">
                                Transparent
                                <span className="badge bg-secondary text-white">pricing</span>
                            </div>
                            <h2 className="heading_text mb-0">Starting prices for a same-day build</h2>
                            <p className="heading_description mt-2" style={{ maxWidth: 660, margin: '8px auto 0' }}>
                                Every site is custom-scoped, so these are starting points. Your exact quote depends on pages,
                                content, and features — we confirm it before any work begins.
                            </p>
                        </div>
                        <div className="row">
                            {PRICING.map((tier) => (
                                <div className="col-lg-3 col-md-6 mt-4" key={tier.name}>
                                    <div style={{
                                        background: '#fff', borderRadius: 14, padding: '28px 24px', height: '100%',
                                        border: `${tier.best ? '2px' : '1px'} solid ${tier.best ? 'var(--bs-primary)' : 'var(--bs-border-color)'}`,
                                        position: 'relative', boxShadow: tier.best ? '0 12px 30px rgba(0,68,235,0.12)' : 'none',
                                    }}>
                                        {tier.best && (
                                            <span style={{
                                                position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                                                background: 'var(--bs-primary)', color: '#fff', fontSize: '.7rem', fontWeight: 700,
                                                letterSpacing: '.05em', textTransform: 'uppercase', padding: '4px 12px', borderRadius: 20, whiteSpace: 'nowrap',
                                            }}>Most Popular</span>
                                        )}
                                        <h3 style={{ fontSize: '1.1rem', color: 'var(--bs-dark)', marginBottom: 4 }}>{tier.name}</h3>
                                        <div style={{ margin: '6px 0 2px' }}>
                                            <span style={{ fontSize: tier.price === 'Custom' ? '1.6rem' : '2rem', fontWeight: 800, color: 'var(--bs-primary)' }}>
                                                {tier.price !== 'Custom' && <span style={{ fontSize: '.9rem', fontWeight: 600, marginRight: 2 }}>from</span>}{tier.price}
                                            </span>
                                            {tier.sub === 'quote' && <span style={{ fontSize: '.8rem', color: 'var(--bs-body-color)' }}> quote</span>}
                                        </div>
                                        <p className="mb-3" style={{ fontSize: '.85rem' }}>{tier.blurb}</p>
                                        <ul className="unordered_list_block" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                            {tier.features.map((f) => (
                                                <li key={f} style={{ fontSize: '.85rem', color: 'var(--bs-dark)', display: 'flex', gap: 8, padding: '4px 0' }}>
                                                    <i className="fa-solid fa-check" aria-hidden="true" style={{ color: 'var(--bs-primary)', marginTop: 3 }}></i>
                                                    <span>{f}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <a href="#qualify" onClick={(e) => { e.preventDefault(); document.getElementById('qualify')?.scrollIntoView({ behavior: 'smooth' }); }}
                                            className={`btn ${tier.best ? 'btn-primary' : 'btn-outline-dark'} w-100 mt-3`} style={{ display: 'block', textAlign: 'center' }}>
                                            <span className="btn_label" data-text="Get a quote">Get a quote</span>
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="row justify-content-center mt-5">
                            <div className="col-lg-9">
                                <div style={{ background: 'var(--bs-primary-bg-subtle)', borderRadius: 12, padding: '22px 26px' }}>
                                    <h3 style={{ fontSize: '1rem', color: 'var(--bs-dark)', marginBottom: 12 }}>What affects your price</h3>
                                    <div className="row">
                                        {PRICE_FACTORS.map((f) => (
                                            <div className="col-md-6" key={f} style={{ fontSize: '.86rem', color: 'var(--bs-dark)', padding: '4px 12px' }}>
                                                <i className="fa-solid fa-circle-dot" aria-hidden="true" style={{ color: 'var(--bs-primary)', marginRight: 8, fontSize: '.7rem' }}></i>{f}
                                            </div>
                                        ))}
                                    </div>
                                    <p className="mb-0 mt-3" style={{ fontSize: '.8rem', color: 'var(--bs-body-color)' }}>
                                        Hosting &amp; domain are billed separately or bundled on request. Final pricing is confirmed in writing before work begins — no surprises.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Qualification form */}
                <section className="section_space bg-light" id="qualify">
                    <div className="container">
                        <div className="heading_block text-center">
                            <div className="heading_focus_text">
                                Can it be done
                                <span className="badge bg-secondary text-white">in a day?</span>
                            </div>
                            <h2 className="heading_text mb-0">Find out in 2 minutes</h2>
                            <p className="heading_description mt-2" style={{ maxWidth: 640, margin: '8px auto 0' }}>
                                Answer a few quick questions about your site. We’ll estimate your timeline instantly and follow up to confirm.
                            </p>
                        </div>

                        <div className="row justify-content-center">
                            <div className="col-lg-8">
                                <style dangerouslySetInnerHTML={{ __html: SDW_CSS }} />
                                <div className="sdw__card">
                                    {status === 'success' ? (
                                        <div className="sdw__done">
                                            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <circle cx="12" cy="12" r="11" fill={GREEN_BG} />
                                                <path d="M7 12.5l3.2 3.2L17 9" stroke={GREEN} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <h3>Got it — your request is in.</h3>
                                            <p>We'll review your scope and get back to you fast with your same-day verdict and next steps.</p>
                                        </div>
                                    ) : (
                                        <form onSubmit={submit}>
                                            <div className="sdw__grid">
                                                <SdwField label="Full Name" required>
                                                    <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Jane Smith" />
                                                </SdwField>
                                                <SdwField label="Email" required>
                                                    <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@company.com" />
                                                </SdwField>
                                                <SdwField label="Phone">
                                                    <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(858) 952-6234" />
                                                </SdwField>
                                                <SdwField label="Business Name">
                                                    <input type="text" value={form.business} onChange={(e) => set('business', e.target.value)} placeholder="Your business" />
                                                </SdwField>
                                            </div>

                                            <SdwField label="How many pages does your site need?" required>
                                                <SdwSegmented options={['1 page', '2–5 pages', '6–10 pages', '10+ pages']} value={form.pages} onChange={(v) => set('pages', v)} />
                                            </SdwField>
                                            <SdwField label="Is your content ready (logo, copy, images)?" required>
                                                <SdwSegmented options={['Yes — it’s ready', 'Partially', 'No — I need help creating it']} value={form.content} onChange={(v) => set('content', v)} />
                                            </SdwField>
                                            <SdwField label="Do you already have a domain?">
                                                <SdwSegmented options={['Yes', 'No / need one']} value={form.domain} onChange={(v) => set('domain', v)} />
                                            </SdwField>
                                            <SdwField label="What features do you need? (select any)">
                                                <SdwChips options={FEATURE_OPTIONS} value={form.features} onToggle={toggleFeature} />
                                            </SdwField>
                                            <SdwField label="Anything else we should know?">
                                                <textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Tell us about your project, deadline, or goals." />
                                            </SdwField>

                                            {verdict && (
                                                <div className="sdw__verdict" style={{ background: VERDICT_META[verdict.level].bg, border: `1.5px solid ${VERDICT_META[verdict.level].line}` }}>
                                                    <div className="sdw__verdicticon" style={{ background: VERDICT_META[verdict.level].fg }}>
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                            <path d="M12 2a10 10 0 100 20 10 10 0 000-20z" stroke="#fff" strokeWidth="1.8" />
                                                            <path d="M12 7v5l3.2 3.2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <p className="sdw__verdictt" style={{ color: VERDICT_META[verdict.level].fg }}>{verdict.label}</p>
                                                        <p className="sdw__verdictd">{verdict.text}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {status === 'error' && (
                                                <div className="sdw__err">
                                                    Please add your name, email, page count, and content status — or email us at info@buildlabconsulting.com.
                                                </div>
                                            )}

                                            <button type="submit" className="sdw__btn" disabled={status === 'submitting'}>
                                                {status === 'submitting' ? 'Sending…' : 'Get My Same-Day Verdict →'}
                                            </button>
                                        </form>
                                    )}
                                </div>
                                <p className="text-center mt-3" style={{ fontSize: '.82rem', color: 'var(--bs-body-color)' }}>
                                    Bigger or more complex site? We build those too — see <Link onClick={ClickHandler} to="/service-single/Web-Development">Web Development</Link>.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default SameDayWebsitesPage;
